import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { filterUndefined } from 'src/app/lego-utils';
import { Design } from 'src/app/models/domain';
import { DesignDataService, MaterialUomDataService } from 'src/app/services';
import { handleError } from './akita-error-helper';
import { ValueState } from './value-state';

export interface IDesignQuery {
  designId?: string;
  equipmentNumber?: string;
}

interface IDesignValue {
  design?: Design;
  query?: IDesignQuery;
}

@Injectable({ providedIn: 'root' })
export class DesignService {
  private readonly state = new ValueState<IDesignValue>({});

  public readonly loading$ = this.state.loading$;
  public readonly error$ = this.state.error$;
  public readonly design$ = this.state.selectValue(({ design }) => design);
  public readonly query$ = this.state.selectValue(({ query }) => query).pipe(filterUndefined());
  public readonly superDesign$ = this.state.selectValue(({ design }) => design?.superDesign);

  public readonly designId$ = this.state
    .selectValue(({ design }) => design?.designId)
    .pipe(filterUndefined());

  public constructor(
    private readonly designDataService: DesignDataService,
    private readonly materialUomDataService: MaterialUomDataService
  ) {}

  public selectQuery(): Observable<IDesignQuery | undefined> {
    return this.state.selectValue(({ query }) => query);
  }

  public selectDesignId(): Observable<string | undefined> {
    return this.state.selectValue(({ design }) => design?.designId);
  }

  public getQuery() {
    return this.state.value.query;
  }

  public getDesign() {
    return this.state.value.design;
  }

  public getDesignId() {
    return this.state.value.design?.designId;
  }

  public loadByDesignId(designId: string): Observable<void> {
    this.state.setLoading(true);

    return this.designDataService.getByDesignId(designId).pipe(
      tap((design) => {
        this.state.update({ design });
        this.state.setLoading(false);
      }),
      catchError((error) => {
        this.state.update({ design: undefined });
        this.state.setLoading(false);
        handleError(this.state, error);

        return of(undefined);
      }),
      map(() => undefined)
    );
  }

  public set40C(designId: string) {
    return this.materialUomDataService.set40C(designId).pipe(
      tap(() => {
        this.state.update(({ design }) => {
          // TODO: using classes instead of interfaces is painful :(
          const clonedDesign = cloneDeep(design);

          if (clonedDesign) {
            clonedDesign.qa40Check = true;
          }

          return { design: clonedDesign };
        });
      })
    );
  }

  public setQuery(query?: IDesignQuery): void {
    this.state.update(({ design }) => ({
      design: query?.designId ? design : undefined,
      query,
    }));
  }

  public clearDesign(): void {
    this.state.update({ design: undefined });
  }
}
